import React, { useState, useEffect } from 'react';
import { 
  Play, Pause, Star, Circle, Square, Triangle, Minus, 
  ArrowRight, Type, Maximize2, ZoomIn, Home, Layers, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const FilmAnalysis = () => {
  // State
  const [activeCategory, setActiveCategory] = useState('shots');
  const [activeSubCategory, setActiveSubCategory] = useState('on-target');
  const [activePlayType, setActivePlayType] = useState('half-court');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(28);
  const [duration] = useState(137); // 2:17 = 137 seconds
  const [selectedTool, setSelectedTool] = useState('cursor');
  const [toolColor, setToolColor] = useState('#f97316'); // orange
  const [toolOpacity, setToolOpacity] = useState(75);
  const [perspective, setPerspective] = useState('front');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [actionFilter, setActionFilter] = useState('all');
  const [quarterFilter, setQuarterFilter] = useState('all');

  // Categories
  const categories = [
    { id: 'free-throws', label: 'Free Throws' },
    { id: 'shots', label: 'Shots' },
    { id: 'turnovers', label: 'Turnovers' },
    { id: 'rebounds', label: 'Rebounds' },
  ];

  const subCategories = {
    'shots': [
      { id: 'in-paint', label: 'In the Paint' },
      { id: 'on-target', label: 'On Target', hasIndicator: true },
      { id: 'wide', label: 'Wide' },
      { id: 'blocked', label: 'Blocked' },
    ],
    'free-throws': [
      { id: 'made', label: 'Made' },
      { id: 'missed', label: 'Missed' },
    ],
    'turnovers': [
      { id: 'steal', label: 'Steal' },
      { id: 'bad-pass', label: 'Bad Pass' },
      { id: 'travel', label: 'Travel' },
    ],
    'rebounds': [
      { id: 'offensive', label: 'Offensive' },
      { id: 'defensive', label: 'Defensive' },
    ],
  };

  const playTypes = [
    { id: 'fast-break', label: 'Fast Break' },
    { id: 'half-court', label: 'Half Court' },
    { id: 'transition', label: 'Transition' },
    { id: 'pick-roll', label: 'Pick and Roll' },
    { id: 'isolation', label: 'Isolation' },
    { id: 'post-up', label: 'Post Up' },
  ];

  // Teams and players
  const teams = [
    { id: 'celtics', abbr: 'BO', color: 'bg-green-600', name: 'Celtics' },
    { id: 'lakers', abbr: 'LA', color: 'bg-purple-600', name: 'Lakers' },
  ];

  const players = [
    { id: 1, number: 0, name: 'J. Tatum', team: 'celtics', position: 'Forward' },
    { id: 2, number: 7, name: 'J. Brown', team: 'celtics', position: 'Guard' },
    { id: 3, number: 9, name: 'D. White', team: 'celtics', position: 'Guard' },
    { id: 4, number: 23, name: 'L. James', team: 'lakers', position: 'Forward' },
    { id: 5, number: 3, name: 'A. Davis', team: 'lakers', position: 'Center' },
  ];

  // Play-by-play data
  const plays = [
    { id: 1, player: 'J. Brown', number: 2, team: 'celtics', action: 'Defensive Rebound', starred: false },
    { id: 2, player: 'L. James', number: 23, team: 'lakers', action: '3-Point Miss', starred: true },
    { id: 3, player: 'J. Tatum', number: 0, team: 'celtics', action: '2-Point Make', starred: false, playing: true },
    { id: 4, player: 'A. Davis', number: 3, team: 'lakers', action: 'Free Throw Make', starred: false },
    { id: 5, player: 'D. White', number: 9, team: 'celtics', action: 'Assist', starred: true },
  ];

  // Drawing tools
  const tools = [
    { id: 'home', icon: Home },
    { id: 'layers', icon: Layers },
    { id: 'settings', icon: Settings },
    { id: 'cursor', icon: null, special: 'cursor' },
    { id: 'pen', icon: null, special: 'pen' },
    { id: 'line', icon: Minus },
    { id: 'arrow', icon: ArrowRight },
    { id: 'circle', icon: Circle },
    { id: 'square', icon: Square },
    { id: 'triangle', icon: Triangle },
    { id: 'text', icon: Type },
    { id: 'rectangle', icon: Maximize2 },
    { id: 'zoom', icon: ZoomIn },
  ];

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle timeline click
  const handleTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    setCurrentTime(Math.floor(percent * duration));
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Film Analysis</h1>
          <p className="text-gray-400 mt-1">Game Film & Video Breakdown</p>
        </div>

        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {/* Category Tabs */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-2 mb-4">
              <div className="flex gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      const subs = subCategories[cat.id];
                      if (subs && subs.length > 0) setActiveSubCategory(subs[0].id);
                    }}
                    className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                      activeCategory === cat.id 
                        ? 'bg-purple-600 text-white' 
                        : 'text-gray-400 hover:bg-gray-800'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sub-category Tabs */}
            {subCategories[activeCategory] && (
              <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-2 mb-4">
                <div className="flex gap-2">
                  {subCategories[activeCategory].map(sub => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveSubCategory(sub.id)}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors relative ${
                        activeSubCategory === sub.id 
                          ? 'bg-gray-700 text-white' 
                          : 'text-gray-400 hover:bg-gray-800'
                      }`}
                    >
                      {sub.label}
                      {sub.hasIndicator && (
                        <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Court View */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl overflow-hidden mb-4">
              {/* Player Badge */}
              <div className="p-4">
                <div className="inline-flex items-center gap-2 bg-gray-800 rounded-full px-3 py-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">0</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">J. Tatum</p>
                    <p className="text-gray-400 text-xs">Forward</p>
                  </div>
                </div>
              </div>

              {/* Court Canvas */}
              <div className="relative h-[400px] bg-gray-900 flex items-center justify-center overflow-hidden">
                {/* Court background text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <h2 className="text-6xl font-bold text-gray-800/30 tracking-widest">
                    CELTICS <span className="text-gray-700/30">vs</span> LAKERS
                  </h2>
                </div>

                {/* Drawing elements - Circle */}
                <div className="absolute left-1/4 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-32 h-32 border-4 border-white/60 rounded-full"></div>
                </div>

                {/* Drawing elements - Rectangle */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/4 -translate-y-1/4">
                  <div className="w-24 h-32 bg-orange-600/80"></div>
                </div>
              </div>

              {/* Play Type Tabs */}
              <div className="p-4 border-t border-gray-800">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {playTypes.slice(0, 3).map(type => (
                    <button
                      key={type.id}
                      onClick={() => setActivePlayType(type.id)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                        activePlayType === type.id 
                          ? 'bg-gray-700 text-white' 
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {playTypes.slice(3).map(type => (
                    <button
                      key={type.id}
                      onClick={() => setActivePlayType(type.id)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                        activePlayType === type.id 
                          ? 'bg-gray-700 text-white' 
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div className="p-4 border-t border-gray-800">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="p-2 text-white hover:bg-gray-800 rounded-full transition-colors"
                  >
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </button>
                  
                  <div 
                    className="flex-1 h-2 bg-gray-800 rounded-full cursor-pointer relative"
                    onClick={handleTimelineClick}
                  >
                    <div 
                      className="h-full bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full"
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    />
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg"
                      style={{ left: `${(currentTime / duration) * 100}%`, marginLeft: '-8px' }}
                    />
                  </div>
                  
                  <span className="text-gray-400 text-sm min-w-[80px] text-right">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
              </div>

              {/* Drawing Tools */}
              <div className="p-4 border-t border-gray-800">
                <div className="flex items-center gap-2">
                  {tools.map((tool, index) => (
                    <React.Fragment key={tool.id}>
                      {index === 3 && <div className="w-px h-6 bg-gray-700 mx-1" />}
                      <button
                        onClick={() => setSelectedTool(tool.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          selectedTool === tool.id 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {tool.special === 'cursor' ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 4l16 8-8 2-2 8z" />
                          </svg>
                        ) : tool.special === 'pen' ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 19l7-7 3 3-7 7-3-3z" />
                            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                          </svg>
                        ) : tool.icon ? (
                          <tool.icon size={16} />
                        ) : null}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-80 flex-shrink-0 space-y-4">
            {/* Tool Properties */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4">Tool Properties</h3>
              
              {/* Color */}
              <div className="mb-4">
                <label className="text-gray-400 text-sm block mb-2">COLOR</label>
                <div 
                  className="w-full h-12 rounded-lg cursor-pointer"
                  style={{ backgroundColor: toolColor }}
                  onClick={() => {
                    const colors = ['#f97316', '#ef4444', '#22c55e', '#3b82f6', '#a855f7', '#ffffff'];
                    const currentIndex = colors.indexOf(toolColor);
                    setToolColor(colors[(currentIndex + 1) % colors.length]);
                  }}
                />
              </div>

              {/* Opacity */}
              <div className="mb-4">
                <label className="text-gray-400 text-sm block mb-2">OPACITY</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={toolOpacity}
                  onChange={(e) => setToolOpacity(parseInt(e.target.value))}
                  className="w-full h-2 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full appearance-none cursor-pointer"
                />
                <p className="text-gray-400 text-sm mt-2">{toolOpacity}%</p>
              </div>

              {/* Perspective */}
              <div>
                <label className="text-gray-400 text-sm block mb-2">PERSPECTIVE</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPerspective('front')}
                    className={`py-6 rounded-lg border-2 transition-colors ${
                      perspective === 'front' 
                        ? 'border-white bg-gray-800' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="w-6 h-8 border-2 border-current mx-auto rounded-sm text-gray-400"></div>
                  </button>
                  <button
                    onClick={() => setPerspective('side')}
                    className={`py-6 rounded-lg border-2 transition-colors ${
                      perspective === 'side' 
                        ? 'border-white bg-gray-800' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="w-6 h-8 border-2 border-current mx-auto rounded-sm text-gray-400 transform rotate-12"></div>
                  </button>
                </div>
              </div>
            </div>

            {/* Filter by Player */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">FILTER BY PLAYER</h3>
                <button className="text-gray-400 hover:text-yellow-400 flex items-center gap-1 text-sm">
                  <Star size={14} /> Starred
                </button>
              </div>

              {/* Team/Player avatars */}
              <div className="flex items-center gap-2 mb-4">
                {teams.map(team => (
                  <button
                    key={team.id}
                    onClick={() => setSelectedPlayer(selectedPlayer === team.id ? null : team.id)}
                    className={`w-10 h-10 ${team.color} rounded-full flex items-center justify-center text-white text-xs font-bold transition-transform ${
                      selectedPlayer === team.id ? 'ring-2 ring-white scale-110' : ''
                    }`}
                  >
                    {team.abbr}
                  </button>
                ))}
                {players.map(player => (
                  <button
                    key={player.id}
                    onClick={() => setSelectedPlayer(selectedPlayer === player.id ? null : player.id)}
                    className={`w-10 h-10 ${player.team === 'celtics' ? 'bg-green-600' : 'bg-purple-600'} rounded-full flex items-center justify-center text-white text-xs font-bold transition-transform ${
                      selectedPlayer === player.id ? 'ring-2 ring-white scale-110' : ''
                    }`}
                  >
                    {player.number}
                  </button>
                ))}
              </div>

              {/* Filters */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="all">All Actions</option>
                  <option value="shots">Shots</option>
                  <option value="rebounds">Rebounds</option>
                  <option value="assists">Assists</option>
                </select>
                <select
                  value={quarterFilter}
                  onChange={(e) => setQuarterFilter(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="all">All Quarter</option>
                  <option value="q1">Q1</option>
                  <option value="q2">Q2</option>
                  <option value="q3">Q3</option>
                  <option value="q4">Q4</option>
                </select>
              </div>

              {/* Play-by-play list */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {plays.map(play => (
                  <div 
                    key={play.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      play.playing ? 'bg-gray-800' : 'hover:bg-gray-800/50'
                    }`}
                  >
                    {play.playing && (
                      <button className="p-1 bg-green-600 rounded-full">
                        <Play size={12} className="text-white" fill="white" />
                      </button>
                    )}
                    <div className={`w-8 h-8 ${play.team === 'celtics' ? 'bg-green-600' : 'bg-purple-600'} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                      {play.team === 'celtics' ? 'BOS' : 'LAL'}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm">
                        <span className="font-semibold">{play.number}</span> {play.player}
                      </p>
                      <p className="text-gray-400 text-xs">{play.action}</p>
                    </div>
                    <button className={`${play.starred ? 'text-yellow-400' : 'text-gray-600'} hover:text-yellow-400`}>
                      <Star size={16} fill={play.starred ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilmAnalysis;
